import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { User, Lock, Eye, EyeOff } from 'lucide-react';
import { Toaster } from '@/components/ui/sonner';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;

const LoginPage = ({ onLogin }) => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await axios.post(`${BACKEND_URL}/api/galleries/verify`, { username, password });
      toast.success('Login successful!');
      onLogin(response.data.access_token, response.data.username, response.data.role);
      navigate('/');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const fieldWrap = { display: 'flex', alignItems: 'center', gap: 10, border: '1px solid #E4E4E9', borderRadius: 9, background: '#FBFBFC', padding: '0 13px', marginBottom: 16 };
  const inputStyle = { flex: 1, border: 'none', outline: 'none', background: 'transparent', fontSize: 14, color: '#141414', padding: '13px 0' };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 font-tac-body" style={{ background: '#EDEDF0' }}>
      <Toaster position="top-center" richColors />
      <div className="w-full" style={{ maxWidth: 1060 }}>
        <div className="flex flex-col md:flex-row overflow-hidden" style={{ borderRadius: 16, boxShadow: '0 18px 48px rgba(20,20,20,0.16)', border: '1px solid #E4E4E9', minHeight: 600 }}>
          {/* Carbon brand panel */}
          <div className="relative overflow-hidden hidden md:flex flex-col justify-between" style={{ width: '46%', background: '#0A0A0B', padding: 46 }}>
            <img src="/turbo-mark-white.png" alt="" className="absolute pointer-events-none" style={{ bottom: -70, right: -50, width: 360, opacity: 0.08, transform: 'rotate(-8deg)' }} />
            <img src="/turbo-logo-white.png" alt="Turbo 360" className="relative self-start" style={{ height: 28, width: 'auto' }} />
            <div className="relative">
              <div className="tac-kicker" style={{ fontSize: 11, letterSpacing: '0.22em', color: '#BF212F' }}>Turbo IQ</div>
              <div className="font-tac-display" style={{ fontWeight: 900, fontSize: 42, lineHeight: 1, letterSpacing: '-0.02em', color: '#fff', marginTop: 14 }}>AI for every shot, every face, every brand.</div>
              <div style={{ fontSize: 14, color: 'rgba(255,255,255,0.55)', marginTop: 16, lineHeight: 1.6, maxWidth: 340 }}>Face search, smart tagging, branded delivery — the operator cockpit behind every gallery.</div>
            </div>
            <div className="relative flex items-center gap-3.5">
              <div className="tiq-signal-bar" style={{ width: 120, height: 4, borderRadius: 2 }} />
              <span className="font-tac-mono" style={{ fontSize: 10, letterSpacing: '0.16em', color: 'rgba(255,255,255,0.4)' }}>OPERATIONS COCKPIT</span>
            </div>
          </div>

          {/* White form */}
          <div className="flex-1 flex flex-col justify-center" style={{ background: '#fff', padding: 'clamp(32px,5vw,52px) clamp(24px,5vw,56px)' }}>
            <div className="tac-kicker" style={{ fontSize: 11, letterSpacing: '0.22em', color: '#BF212F' }}>Sign in</div>
            <div className="font-tac-display" style={{ fontWeight: 800, fontSize: 28, color: '#141414', letterSpacing: '-0.015em', margin: '8px 0 4px' }}>Welcome back</div>
            <div style={{ fontSize: 14, color: '#55555C', marginBottom: 28 }}>Sign in to your Turbo IQ workspace.</div>

            <form onSubmit={handleSubmit}>
              <div style={{ fontSize: 12.5, fontWeight: 600, color: '#55555C', marginBottom: 7 }}>Username</div>
              <div style={fieldWrap}>
                <User className="w-4 h-4" style={{ color: '#9A9AA0' }} />
                <input placeholder="alex.river" value={username} onChange={(e) => setUsername(e.target.value)} required data-testid="login-username-input" style={inputStyle} />
              </div>

              <div style={{ fontSize: 12.5, fontWeight: 600, color: '#55555C', marginBottom: 7 }}>Password</div>
              <div style={{ ...fieldWrap, marginBottom: 24 }}>
                <Lock className="w-4 h-4" style={{ color: '#9A9AA0' }} />
                <input type={showPw ? 'text' : 'password'} placeholder="••••••••" value={password} onChange={(e) => setPassword(e.target.value)} required data-testid="login-password-input" style={inputStyle} />
                <button type="button" onClick={() => setShowPw((s) => !s)} style={{ background: 'transparent', border: 'none', cursor: 'pointer', display: 'flex' }}>
                  {showPw ? <EyeOff className="w-[17px] h-[17px]" style={{ color: '#9A9AA0' }} /> : <Eye className="w-[17px] h-[17px]" style={{ color: '#9A9AA0' }} />}
                </button>
              </div>

              <button type="submit" disabled={loading} data-testid="login-submit-button" className="font-tac-heading w-full text-white" style={{ background: '#BF212F', borderRadius: 9, padding: '14px 24px', fontWeight: 600, fontSize: 14, cursor: 'pointer', opacity: loading ? 0.7 : 1 }}>
                {loading ? 'Signing in…' : 'Sign in'}
              </button>
              <div className="text-center" style={{ marginTop: 18 }}>
                <button type="button" onClick={() => navigate('/forgot-password')} className="font-tac-heading" style={{ fontSize: 13, color: '#BF212F', fontWeight: 600, background: 'transparent', border: 'none', cursor: 'pointer' }}>
                  Forgot your password?
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
